import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const { refresh } = useAuth();

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("ah_token", data.access_token);
      await refresh();
      toast.success(t("auth.welcome_back", { name: data.name }));
      // Dopo il login: admin → SEMPRE pannello admin (anche se era stato
      // reindirizzato da un'altra pagina). Gli altri ruoli tornano alla pagina
      // da cui venivano (o "/" landing personalizzata per ruolo).
      const to = data.role === "admin" ? "/admin" : (loc.state?.from || "/");
      nav(to);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("auth.login_error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[100svh] pt-32 pb-20 bg-cream" data-testid="login-page">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("auth.login_section")}</div>
          <h1 className="mt-4 font-display text-4xl lg:text-5xl font-light text-ink">{t("auth.login_title")}</h1>
          <p className="mt-6 text-slate1 leading-relaxed">
            {t("auth.login_sub")}
          </p>
        </div>

        <form onSubmit={onSubmit} className="bg-white border border-ink/10 p-8 md:p-10 rounded-sm" data-testid="login-form">
          <label className="block text-[11px] tracking-[0.2em] uppercase text-slate1">{t("auth.email")}</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full border border-ink/20 px-4 py-3 bg-cream focus:outline-none focus:border-ink rounded-sm"
            data-testid="login-email"
          />

          <label className="mt-6 block text-[11px] tracking-[0.2em] uppercase text-slate1">{t("auth.password")}</label>
          <div className="relative mt-2">
            <input
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-ink/20 px-4 py-3 pr-12 bg-cream focus:outline-none focus:border-ink rounded-sm"
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate1 hover:text-ink p-1 rounded"
              aria-label={showPassword ? t("auth.hide_password", "Nascondi password") : t("auth.show_password", "Mostra password")}
              tabIndex={-1}
              data-testid="login-toggle-password"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full bg-ink text-cream py-4 text-[12px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-60"
            data-testid="login-submit"
          >
            {loading ? t("auth.login_btn_loading") : t("auth.login_btn")}
          </button>

          <div className="mt-6 flex items-center justify-between text-sm text-slate1">
            <Link to="/forgot-password" className="text-ink underline" data-testid="login-forgot-password">
              {t("auth.forgot_password")}
            </Link>
            <span>{t("auth.no_account")} <Link to="/register" className="text-ink underline">{t("auth.create_account")}</Link></span>
          </div>
        </form>
      </div>
    </div>
  );
}
